import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import * as styles from "./styles.module.scss";
import { IconButton, Stack } from "@mui/material";
import { Clear } from "@mui/icons-material";

const CustomDatePickerIcon = ({ title, ...props }) => (
  <Tooltip title={title} placement="top-end">
    <AccessTimeOutlinedIcon
      style={{ color: "var(--palette-text)", pointerEvents: "auto" }}
      sx={{
        width: "19px",
        marginRight: "6px",
        ...props?.sx,
      }}
      {...props}
    />
  </Tooltip>
);

const UITimePicker = ({
  name,
  value,
  title,
  placeholder,
  onChange,
  onBlur,
  error,
  helperText,
  disabled = false,
  size = "medium",
  sx = {},
  format = "HH:mm",
  tooltip = "",
  iconSx = {},
  clearIconSx = {},
  showClear = false,
  minTime,
  range = false,
  hideIcon = false,
  nonUTC = false,
  required = false,
}) => {
  const now = moment().startOf("minute");
  const oneHourLater = now.clone().add(1, "hour");

  const [startTime, setStartTime] = React.useState(
    range
      ? value?.startTime || now
      : moment(value || undefined, format)
          .year(1970)
          .month(0)
          .date(1) ||
          // .format(time.format.dateAndTime)
          null,
  );
  const [endTime, setEndTime] = React.useState(
    range ? value?.endTime || oneHourLater : null,
  );

  React.useEffect(() => {
    if (value && typeof value == "string") {
      const dates = value?.split(",");
      if (dates.length) {
        dates.forEach((date, i) => {
          const time = moment(date).year(1970).month(0).date(1);
          i == 0 ? setStartTime(time) : setEndTime(time);
        });
      }
    }
  }, []);

  const formatOutput = (start, end) => {
    if (!start & !end) {
      return {
        target: {
          value: "",
          name,
        },
      };
    }
    const output = range
      ? `${start.format(format)},${end.format(format)}`
      : moment(start).startOf("minute"); // start.format(format);
    return {
      target: {
        value: output,
        name,
      },
    };
  };

  const handleSingleChange = (e) => {
    const time = e ? moment(e) : null;
    onChange(formatOutput(time, null));
  };

  const handleSingleBlur = (e) => {
    const time = e ? moment(e).year(1970).month(0).date(1) : null;
    onBlur(formatOutput(time, null));
  };

  const handleStartTimeChange = (e) => {
    const time = e ? moment(e).year(1970).month(0).date(1) : null;
    setStartTime(time);
    const newEndTime = time.clone().add(1, "hour");
    if (!endTime || newEndTime.isSameOrAfter(endTime)) {
      setEndTime(newEndTime);
      onChange(formatOutput(time, newEndTime));
    } else {
      onChange(formatOutput(time, endTime));
    }
  };

  const handleEndTimeChange = (e) => {
    const time = e ? moment(e).year(1970).month(0).date(1) : null;
    if (time.isSameOrAfter(startTime)) {
      setEndTime(time);
      onChange(formatOutput(startTime, time));
    }
  };

  const handleStartTimeBlur = (e) => {
    const time = e ? moment(e).year(1970).month(0).date(1) : null;
    onBlur(formatOutput(time, endTime));
  };

  const handleEndTimeBlur = (e) => {
    const time = e ? moment(e).year(1970).month(0).date(1) : null;
    onBlur(formatOutput(startTime, time));
  };

  const dateTimeFormat = format || "hh:mm A";

  const isValidDate = (value) => {
    return moment(value, moment.ISO_8601, true).isValid();
  };
  const checkValue = (val) => {
    let isValid = isValidDate(val);
    if (val) {
      return nonUTC
        ? isValid
          ? moment.utc(val)
          : moment.utc(val, format)
        : moment(val, format);
    } else {
      return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {range ? (
        <Stack direction="row" spacing={2}>
          <TimePicker
            slots={{
              openPickerIcon: (props) =>
                hideIcon ? (
                  <></>
                ) : (
                  <CustomDatePickerIcon
                    sx={iconSx}
                    title={tooltip}
                    {...props}
                  />
                ),
            }}
            format={dateTimeFormat}
            minTime={minTime}
            slotProps={{
              actionBar: { actions: ["clear"] },
              popper: {
                className: styles.timepickerPaper,
                sx: {
                  "& .MuiPickersLayout-root ul li.Mui-selected": {
                    backgroundColor:
                      " var(--palette-primary_main, var(--palette-theme, var(--blue, #007bff))) !important",
                  },
                },
              },
              textField: {
                required: required,
                size: size,
                placeholder: dateTimeFormat || "",
                helperText: error ? helperText : "",
                InputLabelProps: {
                  style: {
                    color: error
                      ? "#d32f2f"
                      : disabled
                        ? "var(--palette-disabled_button_text)"
                        : "var(--palette-grey_text)",
                  },
                },
                InputProps: {
                  style: {
                    color: "var(--palette-text)",
                    background: disabled
                      ? "var(--palette-disabled_button)"
                      : "var(--palette-grid_table)",
                  },
                },
              },
              openPickerButton: {
                sx: {
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  justifyContent: "flex-end",
                  borderRadius: 0,
                },
              },
            }}
            label={`${title || ""} Start`}
            value={startTime ? moment(startTime) : null}
            onChange={handleStartTimeChange}
            onBlur={handleStartTimeBlur}
            disabled={disabled}
            sx={{ ...sx }}
            clearable
            ampmInClock={false}
          />
          <TimePicker
            slots={{
              openPickerIcon: (props) =>
                hideIcon ? (
                  <></>
                ) : (
                  <CustomDatePickerIcon
                    sx={iconSx}
                    title={tooltip}
                    {...props}
                  />
                ),
            }}
            format={dateTimeFormat}
            minTime={startTime || minTime}
            slotProps={{
              actionBar: { actions: ["clear"] },
              popper: {
                className: styles.timepickerPaper,
                sx: {
                  "& .MuiPickersLayout-root ul li.Mui-selected": {
                    backgroundColor:
                      " var(--palette-primary_main, var(--palette-theme, var(--blue, #007bff))) !important",
                  },
                },
              },
              textField: {
                size: size,
                placeholder: dateTimeFormat || "",
                helperText: error ? helperText : "",
                // style: {
                //   color: "var(--palette-text) !important",
                // },

                InputLabelProps: {
                  style: {
                    color: error
                      ? "#d32f2f"
                      : disabled
                        ? "var(--palette-disabled_button_text)"
                        : "var(--palette-grey_text)",
                  },
                },
                InputProps: {
                  style: {
                    color: "var(--palette-text)",
                    background: disabled
                      ? "var(--palette-disabled_button)"
                      : "var(--palette-grid_table)",
                  },
                },
              },
              openPickerButton: {
                sx: {
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  justifyContent: "flex-end",
                  borderRadius: 0,
                },
              },
            }}
            label={`${title || ""} End`}
            value={endTime ? moment(endTime) : null}
            onChange={handleEndTimeChange}
            onBlur={handleEndTimeBlur}
            disabled={disabled}
            sx={{ ...sx }}
            clearable
            ampmInClock={false}
          />
        </Stack>
      ) : (
        <TimePicker
          slots={{
            openPickerIcon: (props) =>
              hideIcon ? (
                <></>
              ) : (
                <CustomDatePickerIcon sx={iconSx} title={tooltip} {...props} />
              ),
          }}
          format={dateTimeFormat}
          minTime={minTime}
          slotProps={{
            actionBar: { actions: ["clear"] },
            popper: {
              className: styles.timepickerPaper,
              sx: {
                "& .MuiPickersLayout-root ul li.Mui-selected": {
                  backgroundColor: " var(--palette-primary_main, var(--palette-theme, var(--blue, #007bff))) !important",
                },
              },
            },
            textField: {
              value: checkValue(value),
              required: required,
              size: size,
              placeholder: placeholder || dateTimeFormat || "",
              helperText: error ? helperText || error : "",
              style: {
                color: "var(--palette-text)",
              },
              FormHelperTextProps: {
                sx: {
                  color: "#d32f2f !important",
                  position: "absolute",
                  bottom: "-20px",
                },
              },
              InputLabelProps: {
                style: {
                  color: error
                    ? "#d32f2f"
                    : disabled
                      ? "var(--palette-disabled_button_text)"
                      : "var(--palette-grey_text)",
                },
              },
              InputProps: {
                style: {
                  color: "var(--palette-text)",
                  background: disabled
                    ? "var(--palette-disabled_button)"
                    : "var(--palette-grid_table)",
                },
              },
            },
            openPickerButton: {
              sx: {
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: "100%",
                justifyContent: "flex-end",
                borderRadius: 0,
              },
            },
          }}
          name={name}
          label={title || ""}
          value={checkValue(value)}
          onChange={handleSingleChange}
          onAccept={handleSingleChange}
          onBlur={handleSingleBlur}
          disabled={disabled}
          sx={{ ...sx }}
          clearable
          ampm
          ampmInClock={false}
        />
      )}
      {showClear && (range ? startTime || endTime : value) && (
        <IconButton
          sx={{
            position: "absolute",
            right: "26px",
            bottom: "5px",
            ...clearIconSx,
          }}
          onClick={() => {
            if (range) {
              setStartTime(null);
              setEndTime(null);
              handleStartTimeChange(null);
              handleEndTimeChange(null);
            } else {
              handleSingleChange(null);
            }
          }}
        >
          <Clear
            sx={{
              width: "15px",
              height: "15px",
            }}
          />
        </IconButton>
      )}
    </LocalizationProvider>
  );
};

export default UITimePicker;
